// Copy for the QQQ forward P/E page. `{name}` placeholders are filled by fmt().
const translations = {
    en: {
        kicker: 'Nasdaq-100 valuation',
        title: 'QQQ forward P/E',
        subtitle:
            'The Nasdaq-100 index (which QQQ tracks) divided by analysts’ consensus earnings for the '
            + 'next twelve months. Data from History of Market, re-fetched every six hours and kept '
            + 'on this site so the history keeps growing.',
        asOf: 'Latest reading {date} ({basis})',
        basis: { daily: 'daily', weekly: 'weekly' },
        language: 'Language',
        baseline: 'Compare with',
        loading: 'Loading the forward P/E…',
        error: 'Could not load the data. The FastAPI service may be down; try again in a minute.',
        retry: 'Try again',
        unavailable: 'No forward P/E stored yet: the source could not be reached. Check back shortly.',
        links: { bplus: 'B+ strategy signal', monitor: 'QQQ moving averages' },

        windows: { '5y': '5 yrs', '10y': '10 yrs', '20y': '20 yrs', all: 'Since 2001' },
        period: { years: 'the last {n} years', all: 'all readings since {start}' },
        zones: {
            very_low: 'Very low',
            low: 'Low',
            normal: 'Normal range',
            high: 'High',
            very_high: 'Very high',
        },

        now: {
            heading: 'Forward P/E today',
            zoneVs: 'vs {period}',
            desc: 'Higher than {pct} of weekly readings over {period}. Median {median}, middle half {p25} – {p75}.',
            live: 'At the {date} QQQ close of {price} it is about {pe}, with the same earnings estimates.',
            low: 'Low',
            high: 'High',
            median: 'Median',
            middle: 'Middle half',
            today: 'Today',
            eps: 'Forward EPS per QQQ share',
            epsChange: '{v} in 12 months',
            ey: 'Forward earnings yield',
            tbill: '3-month T-bill {v}',
            close: 'QQQ close, {date}',
        },

        chart: {
            heading: 'Forward P/E since 2001',
            note: 'One reading a week until {weeklyEnd}, then one a day. Dashed lines: median and middle half of {period}. '
                + '2001–2004 is distorted by the collapse in earnings after the dot-com bust, and the source has no data from January 2005 to July 2006.',
            ranges: { '1y': '1Y', '3y': '3Y', '5y': '5Y', '10y': '10Y', all: 'All' },
            series: {
                fwd: 'Forward P/E (weekly)',
                fwd_d: 'Forward P/E (daily)',
                median: 'Median',
                p25: '25th percentile',
                p75: '75th percentile',
            },
        },

        levels: {
            heading: 'QQQ at other multiples',
            note: 'Where QQQ would trade if the market paid these multiples for today’s earnings estimates ({eps} per QQQ share). Percentiles are over {period}.',
            level: 'Level',
            multiple: 'Forward P/E',
            price: 'QQQ',
            distance: 'vs today',
            rows: {
                today: 'Today',
                min: 'Low ({date})',
                p25: '25th percentile',
                median: 'Median',
                p75: '75th percentile',
                max: 'High ({date})',
            },
        },

        history: {
            heading: 'Against its own history',
            note: 'Percentile: the share of weekly readings in the period below today’s value.',
            period: 'Period',
            median: 'Median',
            range: 'Low – high',
            pct: 'Percentile',
            names: { '5y': 'Last 5 years', '10y': 'Last 10 years', '20y': 'Last 20 years', all: 'Since {start}' },
        },

        years: {
            heading: 'Year by year',
            year: 'Year',
            end: 'Year-end',
            high: 'High',
            low: 'Low',
            ytd: '{year} so far',
            since: 'from {date}',
        },

        cross: {
            heading: 'Cross-check',
            name: 'WSJ · Birinyi Associates',
            when: 'Weekly, last on {date}',
            forward: 'Forward P/E',
            trailing: 'Trailing P/E',
            growth: 'Implied earnings growth',
            missing: 'The WSJ figure could not be fetched yet.',
            note: 'Sources differ by a point or two because they count earnings differently (operating vs. reported, how fiscal years are blended, missing estimates). Compare each source with its own history, not with another source.',
        },

        about: {
            heading: 'About the data',
            items: [
                'Forward P/E = index level ÷ consensus earnings expected over the next twelve months. QQQ holds the Nasdaq-100, so the index’s multiple is QQQ’s.',
                'Weekly series (April 2001 – {weeklyEnd}): History of Market’s 12-month blended-forward consensus. After that, the site’s own daily calculation from FY1/FY2 consensus earnings weighted by ETF holdings; the two agree within 0.2% on the day they overlap.',
                'Percentiles and the table above use one reading per week. Forward EPS per QQQ share = QQQ close ÷ forward P/E.',
            ],
            credit: 'Data: {source} (CC BY 4.0).',
        },

        disclaimer: 'A research tool, not investment advice. Valuation says little about where prices go in the next few months.',
    },

    zh: {
        kicker: '纳斯达克 100 估值',
        title: 'QQQ 远期市盈率（Forward P/E）',
        subtitle:
            '纳斯达克 100 指数（QQQ 跟踪的指数）除以分析师对未来 12 个月盈利的一致预期。'
            + '数据来自 History of Market，每 6 小时抓取一次并存在本站，历史会一直累积。',
        asOf: '最新读数 {date}（{basis}）',
        basis: { daily: '日度', weekly: '周度' },
        language: '语言',
        baseline: '对比区间',
        loading: '正在加载远期市盈率…',
        error: '数据加载失败，可能是 FastAPI 服务暂时不可用，请稍后再试。',
        retry: '重试',
        unavailable: '暂时还没有数据：数据源连不上，请稍后再来。',
        links: { bplus: 'B+ 策略信号', monitor: 'QQQ 均线监控' },

        windows: { '5y': '5 年', '10y': '10 年', '20y': '20 年', all: '2001 年起' },
        period: { years: '最近 {n} 年', all: '{start} 以来全部数据' },
        zones: {
            very_low: '极低',
            low: '偏低',
            normal: '正常区间',
            high: '偏高',
            very_high: '极高',
        },

        now: {
            heading: '今天的远期市盈率',
            zoneVs: '对比{period}',
            desc: '高于{period}中 {pct} 的周度读数。中位数 {median}，中间一半在 {p25} – {p75}。',
            live: '按 {date} QQQ 收盘价 {price}、盈利预期不变估算，约为 {pe}。',
            low: '最低',
            high: '最高',
            median: '中位数',
            middle: '中间一半',
            today: '今天',
            eps: '每股 QQQ 的预期盈利',
            epsChange: '12 个月 {v}',
            ey: '远期盈利收益率',
            tbill: '3 个月美债 {v}',
            close: 'QQQ 收盘价（{date}）',
        },

        chart: {
            heading: '2001 年以来的远期市盈率',
            note: '{weeklyEnd} 之前每周一个读数，之后每天一个。虚线是{period}的中位数和中间一半区间。'
                + '2001–2004 年因互联网泡沫破裂后盈利塌陷而失真；数据源缺 2005 年 1 月到 2006 年 7 月。',
            ranges: { '1y': '1 年', '3y': '3 年', '5y': '5 年', '10y': '10 年', all: '全部' },
            series: {
                fwd: '远期市盈率（周度）',
                fwd_d: '远期市盈率（日度）',
                median: '中位数',
                p25: '25 分位',
                p75: '75 分位',
            },
        },

        levels: {
            heading: '换个估值，QQQ 值多少',
            note: '按今天的盈利预期（每股 QQQ {eps}），如果市场给出这些倍数，QQQ 对应的价格。分位数按{period}计算。',
            level: '参考位',
            multiple: '远期市盈率',
            price: 'QQQ',
            distance: '相对今天',
            rows: {
                today: '今天',
                min: '最低（{date}）',
                p25: '25 分位',
                median: '中位数',
                p75: '75 分位',
                max: '最高（{date}）',
            },
        },

        history: {
            heading: '和自己的历史比',
            note: '分位：该区间内低于今天读数的周度读数占比。',
            period: '区间',
            median: '中位数',
            range: '最低 – 最高',
            pct: '分位',
            names: { '5y': '最近 5 年', '10y': '最近 10 年', '20y': '最近 20 年', all: '{start} 以来' },
        },

        years: {
            heading: '逐年',
            year: '年份',
            end: '年末',
            high: '最高',
            low: '最低',
            ytd: '{year}（至今）',
            since: '自 {date}',
        },

        cross: {
            heading: '交叉核对',
            name: 'WSJ · Birinyi Associates',
            when: '每周更新，最近一次 {date}',
            forward: '远期市盈率',
            trailing: '过去 12 个月市盈率',
            growth: '隐含盈利增长',
            missing: '暂时还没抓到 WSJ 的数据。',
            note: '不同来源会差一两个点，因为盈利口径不同（经营利润还是报告利润、财年怎么折算、缺失的预期怎么处理）。每个来源只和它自己的历史比，不要跨来源比。',
        },

        about: {
            heading: '关于数据',
            items: [
                '远期市盈率 = 指数点位 ÷ 未来 12 个月的一致预期盈利。QQQ 持有的就是纳斯达克 100，所以指数的市盈率就是 QQQ 的。',
                '周度序列（2001 年 4 月 – {weeklyEnd}）：History of Market 的 12 个月混合远期一致预期。之后是该网站按 FY1/FY2 一致预期和 ETF 持仓权重每天自算的数，两者在重叠那天相差不到 0.2%。',
                '分位数和上面的表格都按每周一个读数计算。每股 QQQ 的预期盈利 = QQQ 收盘价 ÷ 远期市盈率。',
            ],
            credit: '数据：{source}（CC BY 4.0）。',
        },

        disclaimer: '这是研究工具，不是投资建议。估值高低对未来几个月的涨跌几乎没有预测力。',
    },
};

export default translations;
